import { useState, useEffect, useCallback } from "react";
import { adminAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { ArrowLeft, Users, Search, RefreshCw, UserPlus, Pencil, Key, Trash2, Ban, ShieldCheck, TrendingUp, Save, Eye, EyeOff, LogOut, Home } from "lucide-react";
import { toast } from "sonner";

function StatCard({ icon: Icon, label, value, color = "text-green-700" }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col items-center gap-1 shadow-sm">
      <Icon className={`w-5 h-5 ${color}`} />
      <span className={`text-2xl font-bold ${color}`}>{value}</span>
      <span className="text-xs text-slate-500 text-center">{label}</span>
    </div>
  );
}

function AppRevenueCard({ name, amount, users, loading }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col gap-1 shadow-sm">
      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{name}</span>
      {loading ? (
        <div className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin mt-1" />
      ) : (
        <>
          <span className="text-xl font-bold text-green-700">€{(amount || 0).toFixed(2)}</span>
          <span className="text-xs text-slate-400">{users || 0} utenti paganti</span>
        </>
      )}
    </div>
  );
}

const CROSS_APP_APIS = {
  gestionescadenze: "https://gestionescadenze-backend.up.railway.app/api/admin/revenue",
  speaktranslatelive: "https://speaklivetranslate-backend.up.railway.app/api/admin/revenue",
  librifree: "https://librifree-backend.up.railway.app/api/admin/revenue",
  djsengine: "https://djsengine-backend.up.railway.app/api/admin/revenue",
  rosariosettimanale: "https://rosariosettimanale-backend.up.railway.app/api/admin/revenue",
};

/* ── Modal: Nuovo Utente ─────────────────────────────────────────────── */
function NewUserModal({ open, onClose, onCreated }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("user");
  const [plan, setPlan] = useState("free");
  const [saving, setSaving] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  useEffect(() => { if (open) { setEmail(""); setPassword(""); setName(""); setPhone(""); setRole("user"); setPlan("free"); setShowPwd(false); } }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) return;
    setSaving(true);
    try {
      await adminAPI.createUser({ email, password, name, phone, role, plan });
      toast.success("Utente creato!");
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella creazione");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-md" data-testid="new-user-modal">
        <DialogHeader><DialogTitle>Nuovo Utente</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-500">Email *</Label>
            <Input data-testid="new-user-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@esempio.com" className="bg-slate-50 border-slate-200" required />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-500">Password *</Label>
            <div className="relative">
              <Input data-testid="new-user-password" type={showPwd ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min. 6 caratteri" className="bg-slate-50 border-slate-200 pr-10" required />
              <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Nome</Label>
              <Input data-testid="new-user-name" value={name} onChange={(e) => setName(e.target.value)} className="bg-slate-50 border-slate-200" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Telefono</Label>
              <Input data-testid="new-user-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="bg-slate-50 border-slate-200" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Ruolo</Label>
              <select data-testid="new-user-role" value={role} onChange={(e) => setRole(e.target.value)} className="w-full h-9 px-3 rounded-md bg-slate-50 border border-slate-200 text-slate-700 text-sm">
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Piano</Label>
              <select data-testid="new-user-plan" value={plan} onChange={(e) => setPlan(e.target.value)} className="w-full h-9 px-3 rounded-md bg-slate-50 border border-slate-200 text-slate-700 text-sm">
                <option value="free">Free</option>
                <option value="premium">Premium</option>
              </select>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" onClick={onClose} className="text-slate-500">Annulla</Button>
            <Button type="submit" disabled={saving || !email.trim() || !password.trim()} className="bg-green-700 hover:bg-green-800 text-white" data-testid="new-user-submit">
              <UserPlus className="w-4 h-4 mr-1" /> {saving ? "Creazione..." : "Crea Utente"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ── Modal: Modifica Utente ───────────────────────────────────────────── */
function EditUserModal({ open, onClose, userToEdit, onUpdated }) {
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("user");
  const [plan, setPlan] = useState("free");
  const [status, setStatus] = useState("active");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && userToEdit) {
      setName(userToEdit.name || "");
      setSurname(userToEdit.surname || "");
      setPhone(userToEdit.phone || "");
      setRole(userToEdit.role || "user");
      setPlan(userToEdit.plan || "free");
      setStatus(userToEdit.status || "active");
    }
  }, [open, userToEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userToEdit) return;
    setSaving(true);
    try {
      await adminAPI.updateUser(userToEdit.id, { name, surname, phone, role, plan, status });
      toast.success("Utente aggiornato!");
      onUpdated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'aggiornamento");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-md" data-testid="edit-user-modal">
        <DialogHeader><DialogTitle>Modifica Utente</DialogTitle></DialogHeader>
        {userToEdit && <p className="text-sm text-slate-400 -mt-2">{userToEdit.email}</p>}
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Nome</Label>
              <Input data-testid="edit-user-name" value={name} onChange={(e) => setName(e.target.value)} className="bg-slate-50 border-slate-200" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Cognome</Label>
              <Input data-testid="edit-user-surname" value={surname} onChange={(e) => setSurname(e.target.value)} className="bg-slate-50 border-slate-200" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-500">Telefono</Label>
            <Input data-testid="edit-user-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="bg-slate-50 border-slate-200" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Ruolo</Label>
              <select data-testid="edit-user-role" value={role} onChange={(e) => setRole(e.target.value)} className="w-full h-9 px-3 rounded-md bg-slate-50 border border-slate-200 text-slate-700 text-sm">
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Piano</Label>
              <select data-testid="edit-user-plan" value={plan} onChange={(e) => setPlan(e.target.value)} className="w-full h-9 px-3 rounded-md bg-slate-50 border border-slate-200 text-slate-700 text-sm">
                <option value="free">Free</option>
                <option value="premium">Premium</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Stato</Label>
              <select data-testid="edit-user-status" value={status} onChange={(e) => setStatus(e.target.value)} className="w-full h-9 px-3 rounded-md bg-slate-50 border border-slate-200 text-slate-700 text-sm">
                <option value="active">Attivo</option>
                <option value="blocked">Bloccato</option>
              </select>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" onClick={onClose} className="text-slate-500">Annulla</Button>
            <Button type="submit" disabled={saving} className="bg-green-700 hover:bg-green-800 text-white" data-testid="edit-user-submit">
              <Save className="w-4 h-4 mr-1" /> {saving ? "Salvataggio..." : "Salva"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ── Modal: Reset Password ────────────────────────────────────────────── */
function ResetPasswordModal({ open, onClose, userToReset }) {
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setPassword(""); setShowPwd(false); } }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userToReset || password.length < 6) { toast.error("Min. 6 caratteri"); return; }
    setSaving(true);
    try {
      await adminAPI.resetUserPassword(userToReset.id, password);
      toast.success("Password reimpostata!");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel reset");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-sm" data-testid="reset-pwd-modal">
        <DialogHeader><DialogTitle>Reset Password</DialogTitle></DialogHeader>
        {userToReset && <p className="text-sm text-slate-400 -mt-2">{userToReset.email}</p>}
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-500">Nuova Password *</Label>
            <div className="relative">
              <Input data-testid="reset-pwd-input" type={showPwd ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min. 6 caratteri" className="bg-slate-50 border-slate-200 pr-10" required />
              <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" onClick={onClose} className="text-slate-500">Annulla</Button>
            <Button type="submit" disabled={saving || password.length < 6} className="bg-green-700 hover:bg-green-800 text-white" data-testid="reset-pwd-submit">
              <Key className="w-4 h-4 mr-1" /> {saving ? "Reimpostazione..." : "Reimposta"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ── AdminPage ────────────────────────────────────────────────────────── */
export default function AdminPage({ user, onBack, onLogout }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterPiano, setFilterPiano] = useState("tutti");
  const [filterStato, setFilterStato] = useState("tutti");
  const [crossApp, setCrossApp] = useState({
    gestionescadenze: { amount: 0, users: 0, loading: true },
    speaktranslatelive: { amount: 0, users: 0, loading: true },
    librifree: { amount: 0, users: 0, loading: true },
    djsengine: { amount: 0, users: 0, loading: true },
    rosariosettimanale: { amount: 0, users: 0, loading: true },
  });

  const [newUserOpen, setNewUserOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [resetPwdUser, setResetPwdUser] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try { const res = await adminAPI.getUsers(); setUsers(res.data); } catch { toast.error("Errore nel caricamento utenti"); } finally { setLoading(false); }
  }, []);

  const fetchCrossAppRevenue = useCallback(async () => {
    for (const app of Object.keys(CROSS_APP_APIS)) {
      try {
        const res = await fetch(CROSS_APP_APIS[app], { signal: AbortSignal.timeout(5000) });
        if (res.ok) { const data = await res.json(); setCrossApp((prev) => ({ ...prev, [app]: { amount: data.total_revenue || 0, users: data.paying_users || 0, loading: false } })); }
        else { setCrossApp((prev) => ({ ...prev, [app]: { amount: 0, users: 0, loading: false } })); }
      } catch { setCrossApp((prev) => ({ ...prev, [app]: { amount: 0, users: 0, loading: false } })); }
    }
  }, []);

  useEffect(() => { fetchUsers(); fetchCrossAppRevenue(); }, [fetchUsers, fetchCrossAppRevenue]);

  const handleToggleStatus = async (u) => {
    const newStatus = u.status === "active" ? "blocked" : "active";
    try { await adminAPI.updateStatus(u.id, newStatus); setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, status: newStatus } : x))); toast.success(`Utente ${newStatus === "active" ? "attivato" : "bloccato"}`); } catch { toast.error("Errore"); }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Eliminare "${u.email}"? Tutte le sue password verranno eliminate.`)) return;
    try { await adminAPI.deleteUser(u.id); setUsers((prev) => prev.filter((x) => x.id !== u.id)); toast.success("Utente eliminato"); } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  const totale = users.length;
  const attivi = users.filter((u) => u.status === "active").length;
  const bloccati = users.filter((u) => u.status === "blocked").length;
  const free = users.filter((u) => (u.plan || "free") === "free").length;
  const premium = users.filter((u) => (u.plan || "") === "premium").length;
  const admins = users.filter((u) => u.role === "admin").length;
  const crossTotal = (crossApp.gestionescadenze.amount || 0) + (crossApp.speaktranslatelive.amount || 0) + (crossApp.librifree.amount || 0) + (crossApp.djsengine.amount || 0) + (crossApp.rosariosettimanale.amount || 0);

  const filtered = users.filter((u) => {
    const matchSearch = !search || (u.name || "").toLowerCase().includes(search.toLowerCase()) || (u.email || "").toLowerCase().includes(search.toLowerCase());
    const matchPiano = filterPiano === "tutti" || (filterPiano === "free" && (u.plan || "free") === "free") || (filterPiano === "premium" && u.plan === "premium");
    const matchStato = filterStato === "tutti" || (filterStato === "attivo" && u.status === "active") || (filterStato === "bloccato" && u.status === "blocked");
    return matchSearch && matchPiano && matchStato;
  });

  const formatDate = (d) => { if (!d) return "—"; return new Date(d).toLocaleDateString("it-IT"); };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-page">
      <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center gap-4">
        <button onClick={onBack} className="text-slate-400 hover:text-slate-700" data-testid="admin-back-btn" title="Dashboard"><ArrowLeft className="w-5 h-5" /></button>
        <div className="flex items-center gap-2 flex-1">
          <Users className="w-6 h-6 text-green-700" />
          <div>
            <h1 className="text-xl font-bold text-slate-800">Gestione Utenti</h1>
            <p className="text-xs text-slate-500">Amministra utenti, piani e incassi</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={onBack} className="gap-2 border-slate-200" data-testid="admin-home-btn">
          <Home className="w-4 h-4" /> Dashboard
        </Button>
        <Button variant="outline" size="sm" onClick={() => { fetchUsers(); fetchCrossAppRevenue(); }} className="gap-2 border-slate-200">
          <RefreshCw className="w-4 h-4" /> Aggiorna
        </Button>
        <Button size="sm" className="gap-2 bg-green-700 hover:bg-green-800 text-white" data-testid="new-user-button" onClick={() => setNewUserOpen(true)}>
          <UserPlus className="w-4 h-4" /> Nuovo Utente
        </Button>
        {onLogout && (
          <Button variant="outline" size="sm" onClick={onLogout} className="gap-2 border-red-200 text-red-500 hover:bg-red-50 hover:text-red-600" data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4" /> Esci
          </Button>
        )}
      </header>

      <div className="p-6 space-y-6 max-w-screen-xl mx-auto">
        <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
          <StatCard icon={Users} label="Totale Utenti" value={totale} color="text-green-700" />
          <StatCard icon={ShieldCheck} label="Attivi" value={attivi} color="text-emerald-600" />
          <StatCard icon={Ban} label="Bloccati" value={bloccati} color="text-red-500" />
          <StatCard icon={Users} label="Free" value={free} color="text-slate-500" />
          <StatCard icon={Users} label="Premium" value={premium} color="text-amber-500" />
          <StatCard icon={ShieldCheck} label="Admin" value={admins} color="text-indigo-600" />
        </div>

        <div>
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-green-700" />
            <h2 className="text-sm font-semibold text-slate-700">Incassi Tutte le App</h2>
            <span className="ml-auto text-sm font-bold text-green-700">Totale Generale: €{crossTotal.toFixed(2)}</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <AppRevenueCard name="Gestione Password" amount={0} users={premium} loading={false} />
            <AppRevenueCard name="Gestione Scadenze" amount={crossApp.gestionescadenze.amount} users={crossApp.gestionescadenze.users} loading={crossApp.gestionescadenze.loading} />
            <AppRevenueCard name="Speak & Translate Live" amount={crossApp.speaktranslatelive.amount} users={crossApp.speaktranslatelive.users} loading={crossApp.speaktranslatelive.loading} />
            <AppRevenueCard name="LibriFree" amount={crossApp.librifree.amount} users={crossApp.librifree.users} loading={crossApp.librifree.loading} />
            <AppRevenueCard name="DJSEngine" amount={crossApp.djsengine.amount} users={crossApp.djsengine.users} loading={crossApp.djsengine.loading} />
            <AppRevenueCard name="Rosario Settimanale" amount={crossApp.rosariosettimanale.amount} users={crossApp.rosariosettimanale.users} loading={crossApp.rosariosettimanale.loading} />
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Cerca nome o email..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-white border-slate-200" />
          </div>
          <select value={filterPiano} onChange={(e) => setFilterPiano(e.target.value)} className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white text-slate-700">
            <option value="tutti">Tutti i piani</option><option value="free">Free</option><option value="premium">Premium</option>
          </select>
          <select value={filterStato} onChange={(e) => setFilterStato(e.target.value)} className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white text-slate-700">
            <option value="tutti">Tutti gli stati</option><option value="attivo">Attivo</option><option value="bloccato">Bloccato</option>
          </select>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
            <Users className="w-4 h-4 text-slate-500" />
            <span className="font-semibold text-slate-700">Utenti Registrati ({filtered.length})</span>
          </div>
          {loading ? (
            <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-green-600 border-t-transparent rounded-full animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-slate-400 py-20">Nessun utente trovato</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Nome / Email</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Ruolo</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Piano</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Stato</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">WhatsApp</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Data Reg.</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Ultimo Accesso</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((u) => (
                    <tr key={u.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0 ${u.role === "admin" ? "bg-gradient-to-br from-amber-500 to-orange-500" : "bg-gradient-to-br from-green-600 to-green-800"}`}>
                            {(u.name || u.email || "?")[0].toUpperCase()}
                          </div>
                          <div>
                            <div className="font-medium text-slate-800">{u.name || u.email?.split("@")[0] || "—"}</div>
                            <div className="text-xs text-slate-400">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${u.role === "admin" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                          {u.role === "admin" ? "Admin" : "User"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${(u.plan || "free") === "premium" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
                          {(u.plan || "free") === "premium" ? "Premium" : "Free"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${u.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`}>
                          {u.status === "active" ? "Attivo" : "Bloccato"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-xs">
                        {u.whatsapp ? <a href={`https://wa.me/${u.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="text-green-600 hover:underline">{u.whatsapp}</a> : "—"}
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(u.created_at)}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(u.last_login || u.updated_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button data-testid={"edit-btn-" + u.id} onClick={() => setEditUser(u)} className="flex items-center gap-1 text-xs text-slate-600 hover:text-green-700 font-medium px-2 py-1 rounded hover:bg-green-50 transition-colors">
                            <Pencil className="w-3 h-3" /> Modifica
                          </button>
                          <button data-testid={"pwd-btn-" + u.id} onClick={() => setResetPwdUser(u)} className="flex items-center gap-1 text-xs text-slate-600 hover:text-blue-700 font-medium px-2 py-1 rounded hover:bg-blue-50 transition-colors">
                            <Key className="w-3 h-3" /> Password
                          </button>
                          {u.id !== user?.id && (
                            <>
                              <button onClick={() => handleToggleStatus(u)} className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded transition-colors ${u.status === "active" ? "text-amber-600 hover:bg-amber-50" : "text-emerald-600 hover:bg-emerald-50"}`}>
                                {u.status === "active" ? <Ban className="w-3 h-3" /> : <ShieldCheck className="w-3 h-3" />}
                              </button>
                              <button onClick={() => handleDelete(u)} className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 font-medium px-2 py-1 rounded hover:bg-red-50 transition-colors">
                                <Trash2 className="w-3 h-3" /> Elimina
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <NewUserModal open={newUserOpen} onClose={() => setNewUserOpen(false)} onCreated={fetchUsers} />
      <EditUserModal open={!!editUser} onClose={() => setEditUser(null)} userToEdit={editUser} onUpdated={fetchUsers} />
      <ResetPasswordModal open={!!resetPwdUser} onClose={() => setResetPwdUser(null)} userToReset={resetPwdUser} />
    </div>
  );
}
